<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const dialogRef = ref(null);

const STATUS_COMPLETED = 'completed';

const getLabelById = useMapGetter('labels/getLabelById');

const open = () => {
  dialogRef.value?.open();
};

defineExpose({ open });

const audienceLabels = computed(() => {
  if (!props.campaign?.audience) return [];
  return props.campaign.audience
    .filter(a => a.type === 'Label')
    .map(a => getLabelById.value(a.id))
    .filter(Boolean);
});

const triggerRules = computed(() => props.campaign?.trigger_rules || {});

const hasMedia = computed(() => {
  const items = triggerRules.value.media_items;
  if (items && items.length > 0) return true;
  return triggerRules.value.media_url && triggerRules.value.media_type;
});

const mediaList = computed(() => {
  const items = triggerRules.value.media_items;
  if (items && items.length > 0) {
    return items.map((item, index) => ({
      ...item,
      index: index + 1,
      previewUrl: item.media_url?.startsWith('/') ? item.media_url : item.media_url,
    }));
  }
  if (triggerRules.value.media_url && triggerRules.value.media_type) {
    return [{
      media_url: triggerRules.value.media_url,
      media_type: triggerRules.value.media_type,
      caption: triggerRules.value.media_caption || '',
      index: 1,
      previewUrl: triggerRules.value.media_url?.startsWith('/') ? triggerRules.value.media_url : triggerRules.value.media_url,
    }];
  }
  return [];
});

const mediaTypeLabelFor = type => {
  const map = {
    image: 'Imagem',
    video: 'Vídeo',
    audio: 'Áudio',
    document: 'Documento',
  };
  return map[type] || type || '—';
};

const formattedDelay = computed(() => {
  const seconds = parseInt(triggerRules.value.send_delay, 10) || 0;
  if (!seconds) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
});

const formattedScheduledAt = computed(() => {
  if (!props.campaign?.scheduled_at) return '—';
  const date = new Date(props.campaign.scheduled_at * 1000);
  return date.toLocaleString();
});

const isCompleted = computed(() => props.campaign?.campaign_status === STATUS_COMPLETED);

const statusText = computed(() =>
  isCompleted.value
    ? t('CAMPAIGN.DISPARO.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.DISPARO.CARD.STATUS.SCHEDULED')
);

const contactsCount = computed(() => props.campaign?.contacts_count ?? '—');
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    overflow-y-auto
    :title="t('CAMPAIGN.DISPARO.DETAILS.TITLE')"
    :show-confirm-button="false"
  >
    <div v-if="campaign" class="flex flex-col gap-4 text-sm text-n-slate-12">
      <!-- Title -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.CREATE.FORM.TITLE.LABEL') }}
        </span>
        <span class="font-medium">{{ campaign.title }}</span>
      </div>

      <!-- Status + Inbox -->
      <div class="flex items-center gap-3">
        <span
          class="inline-flex items-center h-6 px-2 py-0.5 rounded-md text-xs font-medium"
          :class="isCompleted ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-amber-3 text-n-amber-11'"
        >
          {{ statusText }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.DISPARO.DETAILS.INBOX') }}: {{ campaign.inbox?.name }}
        </span>
      </div>

      <!-- Full message -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.DETAILS.MESSAGE') }}
        </span>
        <div
          v-dompurify-html="formatMessage(campaign.message, false, false, false)"
          class="p-3 text-sm rounded-lg bg-n-alpha-2 max-h-48 overflow-y-auto [&>p]:mb-1"
        />
      </div>

      <!-- Labels -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.DETAILS.LABELS') }}
        </span>
        <div class="flex flex-wrap items-center gap-1.5">
          <span
            v-for="label in audienceLabels"
            :key="label.id"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium"
            :style="{ backgroundColor: `${label.color}20`, color: label.color }"
          >
            <span
              class="w-2 h-2 rounded-full"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
          </span>
          <span v-if="audienceLabels.length === 0" class="text-xs text-n-slate-10">
            —
          </span>
        </div>
      </div>

      <!-- Contact count -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.DETAILS.CONTACTS_COUNT') }}
        </span>
        <span class="font-medium">
          {{ contactsCount }} {{ t('CAMPAIGN.DISPARO.DETAILS.CONTACTS') }}
        </span>
      </div>

      <!-- Media -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.DETAILS.MEDIA') }}
        </span>
        <div v-if="hasMedia" class="flex flex-col gap-3">
          <div
            v-for="media in mediaList"
            :key="media.media_url"
            class="flex flex-col gap-2 p-2 rounded-lg bg-n-alpha-2"
          >
            <div class="flex items-start gap-3">
              <div v-if="media.media_type === 'image' && media.previewUrl" class="w-16 h-16 overflow-hidden rounded-lg bg-n-alpha-3 flex-shrink-0">
                <img :src="media.previewUrl" alt="Preview" class="object-cover w-full h-full" />
              </div>
              <video
                v-else-if="media.media_type === 'video' && media.previewUrl"
                :src="media.previewUrl"
                controls
                class="w-32 h-16 rounded-lg bg-n-alpha-3 flex-shrink-0"
              />
              <audio
                v-else-if="media.media_type === 'audio' && media.previewUrl"
                :src="media.previewUrl"
                controls
                class="w-32 flex-shrink-0"
              />
              <div v-else class="flex items-center gap-2 text-xs text-n-slate-11 flex-shrink-0">
                <i class="i-lucide-paperclip size-3" />
                {{ mediaTypeLabelFor(media.media_type) }}
              </div>
              <div class="flex flex-col gap-0.5 min-w-0">
                <span class="text-xs font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.DISPARO.DETAILS.MEDIA_ITEM', { index: media.index }) }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ t('CAMPAIGN.DISPARO.DETAILS.MEDIA_TYPE') }}: {{ mediaTypeLabelFor(media.media_type) }}
                </span>
                <div v-if="media.caption" class="text-xs text-n-slate-11 truncate max-w-[200px]">
                  {{ t('CAMPAIGN.DISPARO.DETAILS.MEDIA_CAPTION') }}: {{ media.caption }}
                </div>
                <span v-else class="text-xs text-n-slate-10">
                  {{ t('CAMPAIGN.DISPARO.DETAILS.CAPTION_PLACEHOLDER') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <span v-else class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.DISPARO.DETAILS.NO_MEDIA') }}
        </span>
      </div>

      <!-- Send delay -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.DETAILS.SEND_DELAY') }}
        </span>
        <span class="text-sm">{{ formattedDelay }}</span>
      </div>

      <!-- Scheduled at -->
      <div class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11 uppercase tracking-wide">
          {{ t('CAMPAIGN.DISPARO.DETAILS.SCHEDULED_AT') }}
        </span>
        <span class="text-sm">{{ formattedScheduledAt }}</span>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-end w-full">
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.DISPARO.DETAILS.CLOSE')"
          @click="dialogRef?.close()"
        />
      </div>
    </template>
  </Dialog>
</template>
